import LazyVideo from "@/components/LazyVideo";
import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import gsap from 'gsap';
import { useIsMobile } from '@/hooks/use-mobile';
import { useT } from '@/context/ContentContext';

gsap.registerPlugin(ScrollTrigger);


const servicesData = [
  {
    id: 1,
    title: "טיפולי שיער וקרקפת",
    subtitle: "מסורת תאילנדית וערבית",
    description: "שיקום החיוניות של השיער שלך. רהאם משלבת טיפולי שיקום שיער מתקדמים עם שילובי ספא ראש תאילנדי ותמציות צמחים ערביים לטיפול בקרקפת ברמה הגבוה והטובה ביותר לדלילות שיער והתקרחות",
    media: "https://cdn.shopify.com/videos/c/o/v/0ec560a3c566433ea31b13dd7201f211.webm",
    mediaType: "video",
    items: ["עיסוי תאילנדי עם צמחים ערביים", "טיפול בקשקשים", "החלקות אורגניות", "שיקום מאסיבי"]
  },
  {
    id: 2,
    title: "טיפולי פנים",
    subtitle: "טיפוח משולב צמחים וטכנולוגיות",
    description: "טיפולי פנים מתקדמים המשלבים ידע מעולם הרפואה, קוסמטיקה מקצועית, טכנולוגיות מתקדמות ורוקחות טבעית מצמחים ערביים.",
    media: "https://cdn.shopify.com/videos/c/o/v/7c179f614d44422e9bb9ecfdc4ceb037.webm",
    mediaType: "video",
    items: ["טיפולי פילינג מתקדמים", "מיצוק והרמת עור", "פיגמנטציה ואקנה", "עור רגיש וסימני גיל"]
  },
  {
    id: 3,
    title: "אסתטיקה מתקדמת ולייזר",
    subtitle: "דיוק הלייזר וחידוש העור",
    description: "טיפולי לייזר ואסתטיקה מתקדמת לפיגמנטציה, אקנה ופוסט אקנה, צלקות, סימני גיל, קעקועים, סרחי עור ושיקום כללי של העור.",
    media: "https://cdn.shopify.com/videos/c/o/v/ee9067a524544e8f8374b5dcaefed763.webm",
    mediaType: "video",
    items: ["פילינג לייזר CO2", "לייזר לפיגמנטציה", "אקנה ופוסט אקנה", "הסרת קעקועים", "הסרת סרחי עור", "DNA סלמון ואקסוזומים"]
  }
];

interface ServiceCardProps {
  service: typeof servicesData[number];
  index: number;
  total: number;
  variant?: 'carousel' | 'grid';
}

const ServiceCard: React.FC<ServiceCardProps> = ({ service, index, total, variant = 'carousel' }) => {
  const isGrid = variant === 'grid';
  return (
    <article
      className={
        isGrid
          ? 'w-full h-full flex'
          : 'snap-center shrink-0 w-[88vw] sm:w-[70vw] md:w-[60vw] lg:w-[48vw] xl:w-[44vw] max-w-[640px] h-full flex'
      }
    >
      <div className="w-full flex flex-col bg-[#3d1f17] border border-white/10 overflow-hidden">
        {/* Media */}
        <div className={`relative w-full overflow-hidden bg-[#3d1f17] shrink-0 ${isGrid ? 'aspect-[4/5]' : 'aspect-[4/5] sm:aspect-[16/11]'}`}>
          {service.mediaType === 'video' ? (
            <LazyVideo
              src={service.media}
              autoPlay muted loop playsInline
              className="absolute inset-0 w-full h-full object-cover object-center"
             aria-hidden="true" />
          ) : (
            <img
              src={service.media}
              alt={service.title}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          )}
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to top, #3d1f17 0%, rgba(61,31,23,0.95) 15%, rgba(61,31,23,0.7) 30%, rgba(61,31,23,0.35) 45%, rgba(61,31,23,0) 60%)' }} />
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 flex flex-col px-4 md:px-6 pt-4 md:pt-5 text-right relative -mt-24 md:-mt-28 z-10" dir="rtl">
          <p className="text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-[#fbbf25] font-bold mb-2">
            {service.subtitle}
          </p>
          <h3 className="font-serif leading-[0.95] tracking-wide text-[#EAE6D7] mb-3 drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]" style={{ fontSize: 'clamp(1.6rem, 2.4vw + 0.4rem, 2.6rem)' }}>
            {service.title}
          </h3>

          <div className={isGrid
            ? ''
            : 'overflow-y-auto overscroll-contain pr-1 -mr-1 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-[#fbbf25]/30 [&::-webkit-scrollbar-track]:bg-transparent'}
          >
            <p className="text-[12px] md:text-[13px] text-[#EAE6D7]/75 font-light leading-[1.6] mb-4">
              {service.description}
            </p>

            <div className="w-full h-px bg-white/10 mb-3" />

            <ul className="flex flex-wrap gap-1.5 mb-4">
              {service.items.map((item) => (
                <li
                  key={item}
                  className="text-[10px] md:text-[11px] text-[#EAE6D7]/85 px-2 py-0.5 border border-white/15 rounded-full tracking-wide"
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>

        </div>

        {/* CTAs - full width */}
        <div className="mt-auto flex w-full">
          <Link
            to="/services"
            className="flex-[1] bg-white/5 hover:bg-white/10 text-[#EAE6D7] py-3.5 md:py-4 px-3 flex items-center justify-center border-l border-white/10 transition-colors duration-300 group/sec-btn"
          >
            <div className="flex items-center gap-2">
              <Plus size={12} className="text-[#EAE6D7]/40 group-hover/sec-btn:rotate-90 transition-transform duration-300" />
              <span className="text-[10px] font-bold tracking-[0.2em] uppercase whitespace-nowrap">פרטים</span>
            </div>
          </Link>

          <Link
            to="/appointment"
            className="flex-[2.2] bg-[#fbbf25] hover:bg-[#EAE6D7] text-[#412018] py-3.5 md:py-4 px-5 flex items-center justify-center gap-3 transition-all duration-500 group/main-btn relative overflow-hidden"
          >
            <span className="relative z-10 text-[11px] font-bold tracking-[0.2em] uppercase whitespace-nowrap">קביעת תור</span>
            <ArrowLeft size={16} className="relative z-10 group-hover/main-btn:-translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </div>
    </article>
  );
};

export const Services: React.FC = () => {
  const isMobile = useIsMobile();
  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const headingLine1 = useT('home.services.heading.line1', 'מגוון טיפולים המשלבים את');
  const headingHighlight = useT('home.services.heading.highlight', 'הכוח הצמחים');
  const headingLine2a = useT('home.services.heading.line2a', 'עם קידמת');
  const headingLine2b = useT('home.services.heading.line2b', 'המדע והטכנולוגיות.');


  // Word-reveal heading
  useEffect(() => {
    const heading = headingRef.current;
    if (!heading) return;

    const wrapWords = (node: ChildNode) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent ?? '';
        if (!text.trim()) return;
        const frag = document.createDocumentFragment();
        const parts = text.split(/(\s+)/);
        parts.forEach((part) => {
          if (/^\s+$/.test(part)) {
            frag.appendChild(document.createTextNode(part));
          } else if (part.length) {
            const span = document.createElement('span');
            span.className = 'word-reveal inline-block';
            span.style.opacity = '0';
            span.textContent = part;
            frag.appendChild(span);
          }
        });
        node.parentNode?.replaceChild(frag, node);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        if (el.tagName === 'BR') return;
        Array.from(el.childNodes).forEach(wrapWords);
      }
    };

    Array.from(heading.childNodes).forEach(wrapWords);
    const words = Array.from(heading.querySelectorAll<HTMLElement>('.word-reveal'));

    const trigger = words.length
      ? ScrollTrigger.create({
          trigger: heading,
          start: 'top 85%',
          end: 'bottom 50%',
          scrub: true,
          onUpdate: (self) => {
            const p = self.progress;
            const totalW = words.length;
            words.forEach((word, index) => {
              const wp = index / totalW;
              const np = (index + 1) / totalW;
              let opacity = 0;
              if (p >= np) opacity = 1;
              else if (p >= wp) opacity = ((p - wp) / (np - wp));
              word.style.opacity = String(opacity);
            });
          },
        })
      : null;

    ScrollTrigger.refresh();
    return () => { trigger?.kill(); };
  }, []);

  // Reveal shutter
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); }
      },
      { threshold: 0, rootMargin: '0px 0px -10% 0px' }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Track active card by scroll position (mobile carousel only)
  useEffect(() => {
    
    const track = trackRef.current;
    if (!track) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const center = track.scrollLeft + track.clientWidth / 2;
        let bestIdx = 0;
        let bestDist = Infinity;
        Array.from(track.children).forEach((child, i) => {
          const el = child as HTMLElement;
          const c = el.offsetLeft + el.clientWidth / 2;
          const d = Math.abs(c - center);
          if (d < bestDist) { bestDist = d; bestIdx = i; }
        });
        setActiveIndex(bestIdx);
      });
    };
    track.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      track.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [isMobile]);

  // Autoplay (mobile carousel only)
  const isPausedRef = useRef(false);
  const activeIndexRef = useRef(0);
  useEffect(() => { activeIndexRef.current = activeIndex; }, [activeIndex]);

  useEffect(() => {
    
    const track = trackRef.current;
    if (!track) return;

    const pause = () => { isPausedRef.current = true; };
    const resumeSoon = () => {
      window.clearTimeout((resumeSoon as any)._t);
      (resumeSoon as any)._t = window.setTimeout(() => { isPausedRef.current = false; }, 4000);
    };

    track.addEventListener('pointerdown', pause);
    track.addEventListener('pointerup', resumeSoon);
    track.addEventListener('touchstart', pause, { passive: true });
    track.addEventListener('touchend', resumeSoon, { passive: true });

    const interval = window.setInterval(() => {
      if (isPausedRef.current) return;
      if (document.hidden) return;
      const next = (activeIndexRef.current + 1) % servicesData.length;
      scrollToIndex(next);
    }, 5000);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout((resumeSoon as any)._t);
      track.removeEventListener('pointerdown', pause);
      track.removeEventListener('pointerup', resumeSoon);
      track.removeEventListener('touchstart', pause);
      track.removeEventListener('touchend', resumeSoon);
    };
  }, [isMobile]);

  const scrollToIndex = (i: number) => {
    const track = trackRef.current;
    if (!track) return;
    const child = track.children[i] as HTMLElement | undefined;
    if (!child) return;
    const target = child.offsetLeft - (track.clientWidth - child.clientWidth) / 2;
    track.scrollTo({ left: target, behavior: 'smooth' });
  };

  return (
    <section
      id="services"
      ref={sectionRef}
      className="relative w-full text-[#EAE6D7] overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-[#67392A]"
        style={{
          clipPath: isVisible ? 'inset(0% 0% 0% 0%)' : 'inset(100% 0% 0% 0%)',
          transition: 'clip-path 1.8s cubic-bezier(0.77, 0, 0.175, 1)',
          willChange: 'clip-path',
        }}
      />

      <div className="relative z-10 pt-24 md:pt-40 pb-10">
        <div className="container mx-auto px-6 max-w-[1400px]">
          <div className="text-center max-w-4xl mx-auto mb-10 md:mb-16">
            <span className="text-[#fbbf25] text-sm tracking-[0.3em] uppercase block mb-6 font-bold">
              השירותים שלנו
            </span>
            <h2 ref={headingRef} className="font-serif text-[#EAE6D7] leading-[1.1] tracking-wide" style={{ fontSize: 'clamp(2.25rem, 4.5vw + 0.5rem, 5.5rem)' }}>
              {headingLine1}
              <br />
              <span className="text-[#EAE6D7]">{headingHighlight}</span> <span className="text-[#EAE6D7]">{headingLine2a}</span> <span className="text-[#fbbf25] italic">{headingLine2b}</span>
            </h2>
          </div>
        </div>
      </div>

      {/* Desktop grid */}
      <div className="hidden lg:block relative z-10 pb-24">
        <div className="container mx-auto px-6 max-w-[1400px]">
          <div className="grid grid-cols-3 gap-6">
            {servicesData.map((service, idx) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 80, rotateX: -12, scale: 0.94 }}
                whileInView={{ opacity: 1, y: 0, rotateX: 0, scale: 1 }}
                viewport={{ once: true, margin: '-10% 0px' }}
                transition={{
                  duration: 1,
                  delay: idx * 0.18,
                  ease: [0.22, 1, 0.36, 1],
                }}
                style={{ transformPerspective: 1200, transformOrigin: 'center top' }}
                className="flex"
              >
                <ServiceCard
                  service={service}
                  index={idx}
                  total={servicesData.length}
                  variant="grid"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile carousel */}
      <div className="lg:hidden relative z-10 pb-16">
        <div
          ref={trackRef}
          dir="ltr"
          className="flex gap-4 overflow-x-auto overflow-y-hidden snap-x snap-mandatory scroll-smooth px-[6vw] sm:px-[15vw] pb-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden h-[min(82dvh,780px)] min-h-[520px]"
          style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}
        >
          {servicesData.map((service, idx) => (
            <ServiceCard
              key={service.id}
              service={service}
              index={idx}
              total={servicesData.length}
              variant="carousel"
            />
          ))}
        </div>

        {/* Controls + dots */}
        <div className="container mx-auto px-6 max-w-[1280px] mt-2">
          <div className="flex items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <button
                onClick={() => scrollToIndex(Math.max(0, activeIndex - 1))}
                aria-label="הקודם"
                disabled={activeIndex === 0}
                className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-[#EAE6D7]/80 hover:border-[#fbbf25] hover:text-[#fbbf25] disabled:opacity-30 transition-colors"
              >
                <ArrowRight size={16} />
              </button>
              <button
                onClick={() => scrollToIndex(Math.min(servicesData.length - 1, activeIndex + 1))}
                aria-label="הבא"
                disabled={activeIndex === servicesData.length - 1}
                className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-[#EAE6D7]/80 hover:border-[#fbbf25] hover:text-[#fbbf25] disabled:opacity-30 transition-colors"
              >
                <ArrowLeft size={16} />
              </button>
            </div>

            <div className="flex items-center gap-2">
              {servicesData.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => scrollToIndex(i)}
                  aria-label={`עבור לכרטיס ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-500 ${i === activeIndex ? 'w-8 bg-[#fbbf25]' : 'w-3 bg-white/20 hover:bg-white/40'}`}
                />
              ))}
            </div>

            <div className="font-serif italic tabular-nums text-[#fbbf25] text-xl md:text-2xl">
              {String(activeIndex + 1).padStart(2, '0')}
              <span className="text-[#EAE6D7]/40 text-sm mx-1">/</span>
              <span className="text-[#EAE6D7]/40 text-sm">{String(servicesData.length).padStart(2, '0')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
